import {
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { Paper, Alert } from '@mantine/core';
import { useChartHeight } from '@/hooks/useChartHeight';
import { useIsMobile } from '@/hooks/useIsMobile';
import { calculateNiceAxisTicks } from '@/utils/formatters';
import { useTimeAxis } from '@/hooks/useTimeAxis';
import { useMemo, useRef } from 'react';
import { ChartSkeleton } from '@/components/charts/ChartSkeleton';
import { ChartHeader } from '@/components/charts/ChartHeader';
import { CHART_GRID_STROKE, CHART_AXIS_TICK, CHART_LEGEND_STYLE } from '@/utils/chartStyles';
import { CHART_CROSSHAIR_COLOR } from '@/utils/chartColors';
import { ChartTooltipContent } from '@/components/charts/ChartTooltipContent';
import type { PowerDataPoint } from '@/api/power';

interface TotalPowerChartProps {
  data: PowerDataPoint[];
  isLoading?: boolean;
  error?: Error | null;
  expanded?: boolean;
  onToggle?: () => void;
}

const POWER_COLOR = 'var(--mantine-color-orange-6)';

export function TotalPowerChart({
  data,
  isLoading,
  error,
  expanded,
  onToggle,
}: TotalPowerChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartHeight = useChartHeight();
  const isMobile = useIsMobile();

  // Convert string timestamps to numbers for the numeric X-axis
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map(point => ({
      ...point,
      timestamp: new Date(point.timestamp).getTime(),
    }));
  }, [data]);

  // Derive date range from data for time axis
  const { startDate, endDate } = useMemo(() => {
    if (chartData.length === 0) {
      const now = new Date();
      return { startDate: now, endDate: now };
    }
    return {
      startDate: new Date(chartData[0]!.timestamp),
      endDate: new Date(chartData[chartData.length - 1]!.timestamp),
    };
  }, [chartData]);

  const timeAxis = useTimeAxis(startDate, endDate);

  // Calculate Y-axis with nice ticks
  const yAxisConfig = useMemo(() => {
    if (!data || data.length === 0) {
      return calculateNiceAxisTicks(0, 100, 5, false);
    }

    const values = data
      .map(d => d.total_watts)
      .filter((v): v is number => v != null && typeof v === 'number' && !isNaN(v));

    if (values.length === 0) {
      return calculateNiceAxisTicks(0, 100, 5, false);
    }

    // Reduce rather than spread: Math.min(...values) overflows the call stack
    // on long date ranges.
    const min = values.reduce((a, b) => (b < a ? b : a));
    const max = values.reduce((a, b) => (b > a ? b : a));
    return calculateNiceAxisTicks(min, max, 5, false);
  }, [data]);

  // Early returns AFTER all hooks
  if (isLoading) {
    return <ChartSkeleton title="Total Power" />;
  }

  if (error) {
    return (
      <Alert color="red" title="Error loading power data">
        {error.message}
      </Alert>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Alert color="blue" title="No data available">
        No power data found for the selected date range.
      </Alert>
    );
  }

  return (
    <Paper ref={chartRef} p={{ base: 'sm', md: 'lg' }} withBorder radius={12} bg="var(--rdh-card-bg)">
      <ChartHeader
        title="Total Power"
        subtitle={isMobile ? '(Watts)' : undefined}
        chartRef={chartRef}
        expanded={expanded}
        onToggle={onToggle}
      />

      <ResponsiveContainer width="100%" height={chartHeight}>
        <ComposedChart data={chartData}>
          <defs>
            <linearGradient id="powerGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={POWER_COLOR} stopOpacity={0.3} />
              <stop offset="95%" stopColor={POWER_COLOR} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} vertical={false} />

          <XAxis
            dataKey="timestamp"
            type="number"
            domain={[startDate.getTime(), endDate.getTime()]}
            ticks={timeAxis.ticks}
            tickFormatter={timeAxis.formatTick}
            tick={CHART_AXIS_TICK}
            axisLine={false}
            tickLine={false}
            height={isMobile ? 35 : 50}
          />

          <YAxis
            domain={yAxisConfig.domain}
            ticks={yAxisConfig.ticks}
            tickFormatter={yAxisConfig.tickFormatter}
            width={isMobile ? 35 : 60}
            tick={CHART_AXIS_TICK}
            axisLine={false}
            tickLine={false}
          />

          <Tooltip
            cursor={{
              stroke: CHART_CROSSHAIR_COLOR,
              strokeWidth: 1,
              strokeDasharray: '4 4',
            }}
            content={
              <ChartTooltipContent
                valueFormatter={(v) => `${v.toFixed(1)}W`}
              />
            }
          />

          <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={CHART_LEGEND_STYLE} />

          {/* Area fill under power line */}
          <Area
            type="linear"
            dataKey="total_watts"
            stroke="none"
            fill="url(#powerGrad)"
            fillOpacity={1}
            isAnimationActive={false}
            connectNulls={false}
            legendType="none"
          />

          {/* Main power line */}
          <Line
            type="linear"
            dataKey="total_watts"
            stroke={POWER_COLOR}
            strokeWidth={1.5}
            dot={false}
            name="Total Power"
            activeDot={{ r: 6 }}
            connectNulls={false}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Paper>
  );
}
